import { useState, useEffect } from 'react';
import { db } from '../utils/firebaseConfig';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import './Jornadas.css';

const defaultFields = [
  { id: '1', name: 'nombre', label: 'Nombre Completo', type: 'text', required: true, order: 0, options: [] },
  { id: '2', name: 'email', label: 'Correo Electrónico', type: 'email', required: true, order: 1, options: [] },
  { id: '3', name: 'telefono', label: 'Teléfono', type: 'tel', required: true, order: 2, options: [] },
  { id: '4', name: 'experiencia', label: 'Experiencia en Futsal', type: 'select', required: false, order: 3, options: ['Principiante', 'Intermedio', 'Avanzado'] },
  { id: '5', name: 'personas', label: 'Número de Personas', type: 'number', required: false, order: 4, options: [] },
  { id: '6', name: 'comentarios', label: 'Comentarios', type: 'textarea', required: false, order: 5, options: [] }
];

export default function InscripcionesTable() {
  const [inscriptions, setInscriptions] = useState([]);
  const [fields, setFields] = useState(defaultFields);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editingNotes, setEditingNotes] = useState(null);
  const [notesText, setNotesText] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Timeout')), 5000)
      );

      try {
        const fieldsLoadPromise = getDocs(collection(db, 'formConfig'));
        const fieldsSnapshot = await Promise.race([fieldsLoadPromise, timeoutPromise]);

        if (!fieldsSnapshot.empty) {
          const config = fieldsSnapshot.docs[0].data();
          const loadedFields = config.fields || [];
          if (loadedFields.length > 0) {
            const sortedFields = [...loadedFields].sort((a, b) => (a.order || 0) - (b.order || 0));
            setFields(sortedFields);
          } else {
            setFields(defaultFields);
          }
        } else {
          setFields(defaultFields);
        }
      } catch (fieldErr) {
        console.error('Error loading fields:', fieldErr);
        setFields(defaultFields);
      }

      try {
        const inscriptionsLoadPromise = getDocs(collection(db, 'inscriptions'));
        const inscriptionsSnapshot = await Promise.race([inscriptionsLoadPromise, timeoutPromise]);

        const inscriptionsData = inscriptionsSnapshot.docs.map(doc => ({
          id: doc.id,
          firebaseId: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp ? doc.data().timestamp.toDate() : new Date()
        }));

        setInscriptions(inscriptionsData);
      } catch (inscErr) {
        console.error('Error loading inscriptions:', inscErr);
        setInscriptions([]);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteInscription = async (firestoreId) => {
    if (!window.confirm('¿Eliminar esta inscripción?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'inscriptions', firestoreId));
      setInscriptions(prev => prev.filter(i => i.firebaseId !== firestoreId));
    } catch (err) {
      console.error('Error deleting inscription:', err);
      setError('Error al eliminar la inscripción');
    }
  };

  const toggleNotified = async (firestoreId, currentValue) => {
    try {
      await updateDoc(doc(db, 'inscriptions', firestoreId), {
        notified: !currentValue
      });
      setInscriptions(prev =>
        prev.map(i => i.firebaseId === firestoreId ? { ...i, notified: !currentValue } : i)
      );
    } catch (err) {
      console.error('Error updating notified:', err);
      setError('Error al actualizar estado');
    }
  };

  const openNotesEditor = (inscription) => {
    setEditingNotes(inscription.firebaseId);
    setNotesText(inscription.notes || '');
  };

  const saveNotes = async () => {
    try {
      await updateDoc(doc(db, 'inscriptions', editingNotes), { notes: notesText });
      setInscriptions(prev =>
        prev.map(i => i.firebaseId === editingNotes ? { ...i, notes: notesText } : i)
      );
      setEditingNotes(null);
      setNotesText('');
    } catch (err) {
      console.error('Error saving notes:', err);
      setError('Error al guardar notas');
    }
  };

  const cancelNotesEdit = () => {
    setEditingNotes(null);
    setNotesText('');
  };

  const filteredInscriptions = inscriptions.filter(inscription => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const dataStr = JSON.stringify(inscription.data).toLowerCase();
    return dataStr.includes(searchLower);
  });

  const sortedInscriptions = [...filteredInscriptions].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    if (sortBy === 'timestamp') {
      aVal = a.timestamp || new Date(0);
      bVal = b.timestamp || new Date(0);
    } else if (a.data && b.data) {
      aVal = a.data[sortBy] || '';
      bVal = b.data[sortBy] || '';
    }

    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }

    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const getDisplayValue = (value) => {
    if (!value) return '-';
    if (value instanceof Date) return value.toLocaleString('es-ES');
    return String(value);
  };

  const exportCSV = () => {
    if (sortedInscriptions.length === 0) {
      alert('No hay inscripciones para exportar');
      return;
    }

    const headers = ['Fecha', ...fields.map(f => f.label), 'Notificado', 'Notas'];
    const rows = sortedInscriptions.map(insc => [
      insc.timestamp ? insc.timestamp.toLocaleString('es-ES') : '',
      ...fields.map(f => insc.data?.[f.name] || ''),
      insc.notified ? 'Sí' : 'No',
      insc.notes || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inscripciones_${new Date().toISOString().split('T')[0]}.csv`);
    link.click();
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Cargando inscripciones...</div>;
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>
      <h2 style={{ color: '#000', marginBottom: '20px', fontFamily: "'Oswald', sans-serif", fontSize: '24px', fontWeight: 'bold' }}>Inscripciones</h2>

      {error && <div style={{ backgroundColor: '#fee2e2', borderLeft: '4px solid #dc2626', padding: '12px', color: '#991b1b', marginBottom: '20px', borderRadius: '4px' }}>{error}</div>}

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Buscar inscripción..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', maxWidth: '300px', flex: 1, minWidth: '200px' }}
        />
        <button
          onClick={exportCSV}
          style={{ padding: '8px 16px', backgroundColor: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
        >
          Descargar CSV
        </button>
        <button
          onClick={loadData}
          style={{ padding: '8px 16px', backgroundColor: '#667eea', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
        >
          Actualizar
        </button>
      </div>

      {inscriptions.length === 0 ? (
        <div style={{ backgroundColor: '#f8f9fa', padding: '40px', textAlign: 'center', borderRadius: '8px', color: '#999' }}>
          <p style={{ marginBottom: '10px', fontSize: '16px' }}>No hay inscripciones aún</p>
          <p style={{ fontSize: '14px' }}>Las inscripciones aparecerán aquí cuando alguien complete el formulario</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr style={{ backgroundColor: '#000', color: '#fff' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #333' }}>Fecha</th>
                {fields.map(field => (
                  <th
                    key={field.id}
                    style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #333', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onClick={() => {
                      if (sortBy === field.name) {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortBy(field.name);
                        setSortOrder('asc');
                      }
                    }}
                  >
                    {field.label} {sortBy === field.name ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                ))}
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', borderRight: '1px solid #333' }}>Notificado</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #333' }}>Notas</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {sortedInscriptions.map((inscription, idx) => (
                <tr key={inscription.id} style={{ backgroundColor: idx % 2 === 0 ? '#f8f9fa' : '#fff', borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '12px', borderRight: '1px solid #eee', whiteSpace: 'nowrap' }}>
                    {inscription.timestamp ? inscription.timestamp.toLocaleString('es-ES') : '-'}
                  </td>
                  {fields.map(field => (
                    <td key={`${inscription.id}-${field.id}`} style={{ padding: '12px', borderRight: '1px solid #eee', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {getDisplayValue(inscription.data?.[field.name])}
                    </td>
                  ))}
                  <td style={{ padding: '12px', textAlign: 'center', borderRight: '1px solid #eee' }}>
                    <input
                      type="checkbox"
                      checked={inscription.notified || false}
                      onChange={() => toggleNotified(inscription.firebaseId, inscription.notified || false)}
                      style={{ cursor: 'pointer', width: '18px', height: '18px', accentColor: '#e30613' }}
                    />
                  </td>
                  <td style={{ padding: '12px', borderRight: '1px solid #eee', cursor: 'pointer', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }} onClick={() => openNotesEditor(inscription)}>
                    {editingNotes === inscription.firebaseId ? (
                      <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                        <textarea
                          value={notesText}
                          onChange={(e) => setNotesText(e.target.value)}
                          style={{ padding: '8px', border: '1px solid #e30613', borderRadius: '4px', fontSize: '12px', minHeight: '60px', fontFamily: 'inherit' }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); saveNotes(); }}
                            style={{ flex: 1, padding: '4px 8px', backgroundColor: '#4caf50', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                          >
                            Guardar
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); cancelNotesEdit(); }}
                            style={{ flex: 1, padding: '4px 8px', backgroundColor: '#999', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: inscription.notes ? '#333' : '#999' }}>
                        {inscription.notes ? (inscription.notes.length > 50 ? inscription.notes.substring(0, 50) + '...' : inscription.notes) : '+ Agregar nota'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => deleteInscription(inscription.firebaseId)}
                      style={{ padding: '6px 12px', backgroundColor: '#f44336', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: '20px', color: '#666', fontSize: '14px' }}>
        Total: {sortedInscriptions.length} inscripción{sortedInscriptions.length !== 1 ? 'es' : ''}
        {searchTerm && ` (${filteredInscriptions.length} coincidencia${filteredInscriptions.length !== 1 ? 's' : ''})`}
      </div>
    </div>
  );
}
